import { useEffect, useRef, useState } from 'react';
import { Alert, App, Button, Modal, Popconfirm, Progress, Radio, Skeleton, Space, Statistic, Table, Tag, Typography } from 'antd';
import { MERGE_SELECTED_TIER } from '@crm/shared';
import type { MassMergeOptions, MassMergeRun, MassMergeSkippedGroup } from '@crm/shared';
import { errorMessage } from '../../api/client';
import { palette } from '../../app/theme';
import { LoadError } from '../../components/states';
import { fmtDateTime, plural } from '../../utils/format';
import { useLatestMassMerge, useMassMergePreview, useRefreshAfterMerge, useStartMassMerge, useStopMassMerge } from '../../features/dedupe/api';
import { EntityTag } from '../../components/EntityTag';

const MATCH = 'They share an email address or a phone number (the last 9 digits, so +1 555 010 2000 and 555-010-2000 match)';
const TIERS = [
  { tier: 1, title: 'Exact copies', description: `${MATCH}, and every other field is the same: blank matches only blank; case and extra spaces don’t count.` },
  { tier: 2, title: 'Same email or phone number', description: `${MATCH}. Values that are alike are kept once and blanks are filled from the other records.` },
  { tier: 3, title: 'Same name and organization', description: 'Contacts with the same first and last name sharing an organization, even without a shared email or phone (such as a company switchboard number shared by many contacts). Every other field must be the same: blank matches only blank; case and extra spaces don’t count.' },
  { tier: 4, title: 'Same name and organization, filling blanks', description: 'Contacts with the same first and last name sharing an organization, where one record may have details the other lacks (an email, a job title). Values that are alike are kept once and blanks are filled from the other records.' },
];

const RULES: Record<number, string[]> = {
  1: [
    'The oldest contact of each group stays; the others are retired into it and keep pointing to it.',
    'Their email addresses, phone numbers and organizations are combined into the contact’s lists; multi-select values are combined.',
    'Groups whose records differ in any field are left alone; Tier 2 can merge those.',
  ],
  2: [
    'The oldest contact of each group stays; the others are retired into it and keep pointing to it.',
    'Values that are the same or at least 90% alike (ignoring case, accents and punctuation) are kept once.',
    'Fields that are empty on one record are filled from the others.',
    'Every email address, phone number and organization goes into the contact’s lists; multi-select values are combined.',
  ],
  3: [
    'The oldest contact of each group stays; the others are retired into it and keep pointing to it.',
    'Their email addresses, phone numbers, organizations and multi-select values are combined into one list each.',
    'Groups whose records differ in any other field are left alone; Tier 4 can merge those.',
  ],
  4: [
    'The oldest contact of each group stays; the others are retired into it and keep pointing to it.',
    'Values that are the same or at least 90% alike (ignoring case, accents and punctuation) are kept once.',
    'Fields that are empty on one record are filled from the others.',
    'Every email address, phone number and organization goes into the contact’s lists; multi-select values are combined.',
  ],
};
const MOVES = 'Every tag, list and segment of each record is kept on the survivor; deals, tasks, notes, emails and form submissions move over, and a snapshot of each retired record stays on the timeline.';

const done = (r: MassMergeRun) => r.mergedGroups + r.conflictGroups + r.skippedGroups + r.failedGroups;

/** Merges every duplicate group of a tier at once, in the background, then shows what was left for review and why. */
export function MassMergeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const latest = useLatestMassMerge();
  const run = latest.data;
  const running = run?.status === 'RUNNING';
  const [view, setView] = useState<'setup' | 'run'>('setup');
  useEffect(() => {
    if (open) setView(running ? 'run' : 'setup');
    // only when the dialog opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // merges happen on the worker: refresh the queue as they come in
  const refresh = useRefreshAfterMerge();
  const seen = useRef<string | null>(null);
  useEffect(() => {
    const key = run ? `${run.id}:${run.mergedGroups}:${run.status}` : null;
    if (seen.current && key && key !== seen.current) refresh();
    seen.current = key;
  }, [run, refresh]);

  return (
    <Modal title="Mass merge duplicates" open={open} onCancel={onClose} width={760} destroyOnHidden footer={null}>
      {latest.isError ? (
        <LoadError error={latest.error} onRetry={latest.refetch} title="Could not load the mass merge" />
      ) : latest.isPending ? (
        <Skeleton active />
      ) : view === 'run' && run ? (
        <RunView run={run} onClose={onClose} onNew={() => setView('setup')} />
      ) : (
        <SetupView open={open} last={run ?? null} onStarted={() => setView('run')} onShowLast={() => setView('run')} onClose={onClose} />
      )}
    </Modal>
  );
}

function SetupView({ open, last, onStarted, onShowLast, onClose }: { open: boolean; last: MassMergeRun | null; onStarted: () => void; onShowLast: () => void; onClose: () => void }) {
  const { message } = App.useApp();
  const [tier, setTier] = useState(1);
  const [conflicts, setConflicts] = useState<MassMergeOptions['conflicts']>('skip');
  const preview = useMassMergePreview(tier, open);
  const start = useStartMassMerge();
  const p = preview.data;

  const submit = () =>
    start
      .mutateAsync({ tier: tier as 1 | 2 | 3 | 4, conflicts })
      .then(() => onStarted())
      .catch((e) => message.error(errorMessage(e)));

  return (
    <>
      {last?.status === 'RUNNING' && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} title="A mass merge is running" action={<Button size="small" onClick={onShowLast}>Show progress</Button>} />
      )}
      <Typography.Text strong>Tier</Typography.Text>
      <Radio.Group value={tier} onChange={(e) => setTier(e.target.value)} style={{ display: 'grid', gap: 8, margin: '8px 0 16px' }}>
        {TIERS.map((t) => (
          <Radio key={t.tier} value={t.tier} style={{ border: `1px solid ${tier === t.tier ? palette.primary : palette.border}`, background: tier === t.tier ? palette.primarySoft : undefined, borderRadius: 12, padding: 14, margin: 0 }}>
            <Space size={6}>
              <Tag color="blue">Tier {t.tier}</Tag>
              <Typography.Text strong>{t.title}</Typography.Text>
            </Space>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t.description}</Typography.Text>
            </div>
            {tier === t.tier && <div style={{ marginTop: 6 }}>
              {preview.isError ? (
                <Typography.Text type="danger">{errorMessage(preview.error)}</Typography.Text>
              ) : !p ? (
                <Typography.Text type="secondary">Counting duplicates…</Typography.Text>
              ) : (
                <Typography.Text>
                  <b>{plural(p.groups, 'group')}</b> {tier === 2 || tier === 4 ? 'of duplicates' : 'of exact copies'}: {plural(p.records, 'contact')} would become {plural(p.groups, 'contact')} ({p.retired.toLocaleString()} retired).
                </Typography.Text>
              )}
            </div>}
          </Radio>
        ))}
      </Radio.Group>

      <Typography.Text strong>How records are combined</Typography.Text>
      <ul style={{ margin: '6px 0 16px', paddingLeft: 20, color: palette.textSecondary }}>
        {[...RULES[tier], MOVES].map((r) => <li key={r}>{r}</li>)}
      </ul>

      {(tier === 2 || tier === 4) && <>
      <Typography.Text strong>When records hold different values</Typography.Text>
      <div style={{ margin: '4px 0 2px' }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>For example two different first names or job titles in one group of duplicates.</Typography.Text>
      </div>
      <Radio.Group value={conflicts} onChange={(e) => setConflicts(e.target.value)} style={{ display: 'grid', gap: 6, margin: '6px 0 16px' }}>
        <Radio value="skip">
          Leave the group for review <Tag color="green">No data loss</Tag>
          <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>Those duplicates stay in the queue to merge one by one; the run lists them with the fields that differ.</Typography.Text></div>
        </Radio>
        <Radio value="keep_oldest">
          Merge anyway, keeping the oldest record’s value
          <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>The other values are only kept in the merge snapshot on the contact’s timeline.</Typography.Text></div>
        </Radio>
      </Radio.Group>
      </>}

      {p && (p.tooLarge > 0 || p.sharedPlaceholders > 0) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title="Some matches are left alone"
          description={[
            p.sharedPlaceholders ? `${plural(p.sharedPlaceholders, 'email address or phone number', 'email addresses or phone numbers')} shared by more than 10 contacts (placeholders such as 000 000 0000) ${p.sharedPlaceholders === 1 ? 'is' : 'are'} not used to match.` : '',
            p.tooLarge ? `${plural(p.tooLarge, 'group')} with more than 10 contacts ${p.tooLarge === 1 ? 'is' : 'are'} skipped.` : '',
          ].filter(Boolean).join(' ')}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div>
          {last && last.status !== 'RUNNING' && (
            <Button type="link" style={{ padding: 0 }} onClick={onShowLast}>
              Last run ({last.tier === MERGE_SELECTED_TIER ? 'selected pairs' : `tier ${last.tier}`}) {fmtDateTime(last.createdAt)}: {plural(last.mergedGroups, 'group')} merged
            </Button>
          )}
        </div>
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Popconfirm
            title={`Merge ${p ? plural(p.groups, 'group') : 'all groups'} now?`}
            description="It runs in the background and can be stopped, but merges can't be undone in bulk."
            okText="Start merging"
            onConfirm={submit}
            disabled={!p?.groups || last?.status === 'RUNNING'}
          >
            <Button type="primary" loading={start.isPending} disabled={!p?.groups || last?.status === 'RUNNING'}>
              {p ? `Merge ${plural(p.groups, 'group')}` : 'Merge'}
            </Button>
          </Popconfirm>
        </Space>
      </div>
    </>
  );
}

const REASON: Record<MassMergeSkippedGroup['reason'], { color: string; label: (g: MassMergeSkippedGroup) => string }> = {
  conflict: { color: 'warning', label: (g) => `Different ${g.fields?.join(', ').toLowerCase() || 'values'}` },
  too_large: { color: 'default', label: (g) => `Too large (${g.size ?? g.ids.length} contacts)` },
  dismissed: { color: 'default', label: () => 'Marked as not duplicates' },
  failed: { color: 'error', label: (g) => `Failed: ${g.error ?? 'unknown error'}` },
};

function RunView({ run, onClose, onNew }: { run: MassMergeRun; onClose: () => void; onNew: () => void }) {
  const { message } = App.useApp();
  const stop = useStopMassMerge();
  const running = run.status === 'RUNNING';
  const processed = done(run);
  const percent = run.totalGroups ? Math.floor((processed / run.totalGroups) * 100) : 0;
  const notMerged = run.conflictGroups + run.skippedGroups + run.failedGroups;
  // the exact tiers (1, 3) do not list the groups that are not exact copies (there may be thousands); the next tier fills blanks
  const exact = run.tier === 1 || run.tier === 3;
  const listed = exact ? run.skippedGroups + run.failedGroups : notMerged;
  const leftNote = [
    exact && run.conflictGroups
      ? `${plural(run.conflictGroups, 'group')} ${run.conflictGroups === 1 ? 'is' : 'are'} not an exact copy; Tier ${run.tier + 1} can merge ${run.conflictGroups === 1 ? 'it' : 'them'} if nothing would be lost.`
      : '',
    listed ? `${plural(listed, 'group')} ${listed === 1 ? 'was left as it is; it is' : 'were left as they are; they are'} listed below.` : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      {running ? (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} title={run.heartbeatAt ? 'Merging in the background' : 'Waiting for the background worker…'} description="You can close this window; the run continues and its progress shows on the Duplicates page." />
      ) : run.status === 'COMPLETED' ? (
        <Alert type={run.failedGroups ? 'warning' : 'success'} showIcon style={{ marginBottom: 16 }} title={`Done: ${plural(run.mergedGroups, 'group')} merged, ${plural(run.mergedRecords, 'contact')} retired`} description={leftNote || undefined} />
      ) : run.status === 'STOPPED' ? (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={`Stopped after ${plural(run.mergedGroups, 'merged group')}`} description="Groups merged so far stay merged. Start again to merge the rest." />
      ) : (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} title="The mass merge failed" description={run.error ?? undefined} />
      )}

      <Progress percent={percent} status={running ? 'active' : run.status === 'FAILED' ? 'exception' : run.status === 'STOPPED' ? 'normal' : 'success'} format={() => `${processed.toLocaleString()} / ${run.totalGroups.toLocaleString()}`} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, margin: '16px 0' }}>
        <Statistic title="Groups merged" value={run.mergedGroups} />
        <Statistic title="Contacts retired" value={run.mergedRecords} />
        <Statistic title={exact ? 'Not exact copies' : 'Different values'} value={run.conflictGroups} styles={{ content: run.conflictGroups ? { color: palette.warning } : undefined }} />
        <Statistic title="Skipped" value={run.skippedGroups} />
        <Statistic title="Failed" value={run.failedGroups} styles={{ content: run.failedGroups ? { color: palette.error } : undefined }} />
      </div>

      {run.skipped.length > 0 && (
        <>
          <Typography.Text strong>Not merged</Typography.Text>
          {run.skipped.length < listed && <Typography.Text type="secondary"> (first {run.skipped.length.toLocaleString()} of {listed.toLocaleString()})</Typography.Text>}
          {!exact && run.conflictGroups > 0 && (
            <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>Groups with different values stay in the review queue to merge one by one.</Typography.Text></div>
          )}
          <Table<MassMergeSkippedGroup>
            rowKey={(g) => g.ids.join(':')}
            size="small"
            style={{ marginTop: 8 }}
            dataSource={run.skipped}
            pagination={run.skipped.length > 10 ? { pageSize: 10, size: 'small', showSizeChanger: false } : false}
            columns={[
              {
                title: 'Contacts',
                key: 'contacts',
                render: (_, g) => (
                  <Space size={4} wrap>
                    {g.ids.map((id, i) => <EntityTag key={id} kind="contact" id={id} onClick={onClose}>{g.names[i] ?? 'Contact'}</EntityTag>)}
                  </Space>
                ),
              },
              { title: 'Why', key: 'why', render: (_, g) => <Tag color={REASON[g.reason].color} style={{ whiteSpace: 'normal' }}>{REASON[g.reason].label(g)}</Tag> },
            ]}
          />
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        {running ? (
          <Popconfirm title="Stop merging?" description="Groups merged so far stay merged." okText="Stop" onConfirm={() => stop.mutateAsync(run.id).catch((e) => message.error(errorMessage(e)))}>
            <Button danger loading={stop.isPending}>Stop</Button>
          </Popconfirm>
        ) : (
          <Button onClick={onNew}>New mass merge</Button>
        )}
        <Button type="primary" onClick={onClose}>Close</Button>
      </div>
    </>
  );
}
